import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button, Radio, Tabs } from 'antd';
import ContentLayout from '../../layout/ContentLayout';
import styles from '../../styles/webshopAndProducts.module.scss';
import GenericTable from '../../components/tables/GenericTable';
import CategoriesTable from '../../components/tables/CategoriesTable';
import SubscriptionPackagesTable from '../../components/tables/SubscriptionPackagesTable';
import Pagination from '../../components/layout/Pagination';
import DeleteModal from '../../components/modals/DeleteModal';
import ProductQuickSettingsModal from '../../components/modals/ProductQuickSettingsModal';
import {
  fetchProducts,
  updateIsProductHidden,
  updateProductHasQuantityDiscount,
  updateProductIsEligibleToBuyWithPoints,
  updateProductIsConfigurationProduct,
  updateProductIsMix4IndividuallySellable,
  updateProductIsExternalProduct,
  updateProductIsSubscriptionOnly,
  updateProductIsFrozenProduct,
  updateProductSkipReferredDiscount,
  hideProduct,
  restoreProduct,
  updateProductStockStatus,
} from '../../api/webshop-products-orders-api';
import ToggleSwitch from '../../components/elements/toggleSwitch/ToggleSwitch';
import { toast } from 'react-toastify';
import { formatPriceWithCurrency } from '../../utils/formatPrice';
import Spinner from '../../components/elements/Spinner/Spinner';
import { stockStatusTranslations } from '../../constants/product-constants';

const ProizvodiTab = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('active'); // 'active' | 'hidden'
  const [hideTarget, setHideTarget] = useState(null);
  const [hiding, setHiding] = useState(false);
  const [quickSettingsTarget, setQuickSettingsTarget] = useState(null);

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', currentPage);
      return next;
    }, { replace: true });
  }, [currentPage]); // eslint-disable-line

  const loadProducts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await fetchProducts({
      page: currentPage,
      limit: 10,
      isDeleted: view === 'hidden',
    });
    if (!error) {
      setProducts(data.products);
      setTotalPages(data.totalPages);
    }
    setLoading(false);
  }, [currentPage, view]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleViewChange = (e) => {
    setView(e.target.value);
    setCurrentPage(1);
  };

  const handleHideToggle = async (row, isEnabled) => {
    const productId = row._id;
    const newValueForHidden = !isEnabled;
    try {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isProductHidden: newValueForHidden } : p)
      );
      const { success } = await updateIsProductHidden(productId, newValueForHidden);
      if (!success) {
        setProducts((prev) =>
          prev.map((p) => p._id === productId ? { ...p, isProductHidden: !newValueForHidden } : p)
        );
        toast.error('Došlo je do greške pri ažuriranju proizvoda.');
      } else {
        toast.success('Uspešno ažuriran proizvod.');
      }
    } catch {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isProductHidden: !newValueForHidden } : p)
      );
      toast.error('Došlo je do greške pri ažuriranju proizvoda.');
    }
  };

  const handleConfigurationProductToggle = async (row, isEnabled) => {
    const productId = row._id;
    try {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isConfigurationProduct: isEnabled } : p)
      );
      const { success } = await updateProductIsConfigurationProduct(productId, isEnabled);
      if (!success) {
        setProducts((prev) =>
          prev.map((p) => p._id === productId ? { ...p, isConfigurationProduct: !isEnabled } : p)
        );
        toast.error('Došlo je do greške pri ažuriranju konfiguracionog proizvoda.');
      } else {
        toast.success('Uspešno ažuriran konfiguracioni proizvod.');
      }
    } catch {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isConfigurationProduct: !isEnabled } : p)
      );
      toast.error('Došlo je do greške pri ažuriranju konfiguracionog proizvoda.');
    }
  };

  const handleMix4IndividuallySellableToggle = async (row, isEnabled) => {
    const productId = row._id;
    try {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isMix4IndividuallySellable: isEnabled } : p)
      );
      const { success } = await updateProductIsMix4IndividuallySellable(productId, isEnabled);
      if (!success) {
        setProducts((prev) =>
          prev.map((p) => p._id === productId ? { ...p, isMix4IndividuallySellable: !isEnabled } : p)
        );
        toast.error('Došlo je do greške pri ažuriranju pojedinačne prodaje Mix4 proizvoda.');
      } else {
        toast.success('Uspešno ažurirana pojedinačna prodaja Mix4 proizvoda.');
      }
    } catch {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isMix4IndividuallySellable: !isEnabled } : p)
      );
      toast.error('Došlo je do greške pri ažuriranju pojedinačne prodaje Mix4 proizvoda.');
    }
  };

  const handleFrozenProductToggle = async (row, isEnabled) => {
    const productId = row._id;
    try {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isFrozenProduct: isEnabled } : p)
      );
      const { success } = await updateProductIsFrozenProduct(productId, isEnabled);
      if (!success) {
        setProducts((prev) =>
          prev.map((p) => p._id === productId ? { ...p, isFrozenProduct: !isEnabled } : p)
        );
        toast.error('Došlo je do greške pri ažuriranju zamrznutog proizvoda.');
      } else {
        toast.success('Uspešno ažuriran zamrznuti proizvod.');
      }
    } catch {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isFrozenProduct: !isEnabled } : p)
      );
      toast.error('Došlo je do greške pri ažuriranju zamrznutog proizvoda.');
    }
  };

  const handleSkipReferredDiscountToggle = async (row, isEnabled) => {
    const productId = row._id;
    try {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, skipReferredDiscount: isEnabled } : p)
      );
      const { success } = await updateProductSkipReferredDiscount(productId, isEnabled);
      if (!success) {
        setProducts((prev) =>
          prev.map((p) => p._id === productId ? { ...p, skipReferredDiscount: !isEnabled } : p)
        );
        toast.error('Došlo je do greške pri ažuriranju opcije isključenja 5% popusta.');
      } else {
        toast.success('Uspešno ažurirana opcija isključenja 5% popusta.');
      }
    } catch {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, skipReferredDiscount: !isEnabled } : p)
      );
      toast.error('Došlo je do greške pri ažuriranju opcije isključenja 5% popusta.');
    }
  };

  const handleQuantityDiscountToggle = async (row, isEnabled) => {
    const productId = row._id;
    try {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, hasQuantityDiscount: isEnabled } : p)
      );
      const { success } = await updateProductHasQuantityDiscount(productId, isEnabled);
      if (!success) {
        setProducts((prev) =>
          prev.map((p) => p._id === productId ? { ...p, hasQuantityDiscount: !isEnabled } : p)
        );
        toast.error('Došlo je do greške pri ažuriranju količinskog popusta.');
      } else {
        toast.success('Uspešno ažuriran količinski popust.');
      }
    } catch {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, hasQuantityDiscount: !isEnabled } : p)
      );
      toast.error('Došlo je do greške pri ažuriranju količinskog popusta.');
    }
  };

  const handleEligibleToBuyWithPointsToggle = async (row, isEnabled) => {
    const productId = row._id;
    try {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isEligibleToBuyWithPoints: isEnabled } : p)
      );
      const { success } = await updateProductIsEligibleToBuyWithPoints(productId, isEnabled);
      if (!success) {
        setProducts((prev) =>
          prev.map((p) => p._id === productId ? { ...p, isEligibleToBuyWithPoints: !isEnabled } : p)
        );
        toast.error('Došlo je do greške pri ažuriranju mogućnosti kupovine poenima.');
      } else {
        toast.success('Uspešno ažurirana mogućnost kupovine poenima.');
      }
    } catch {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isEligibleToBuyWithPoints: !isEnabled } : p)
      );
      toast.error('Došlo je do greške pri ažuriranju mogućnosti kupovine poenima.');
    }
  };

  const handleExternalProductToggle = async (row, isEnabled) => {
    const productId = row._id;
    try {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isExternalProduct: isEnabled } : p)
      );
      const { success } = await updateProductIsExternalProduct(productId, isEnabled);
      if (!success) {
        setProducts((prev) =>
          prev.map((p) => p._id === productId ? { ...p, isExternalProduct: !isEnabled } : p)
        );
        toast.error('Došlo je do greške pri ažuriranju eksternog proizvoda.');
      } else {
        toast.success('Uspešno ažuriran eksterni proizvod.');
      }
    } catch {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isExternalProduct: !isEnabled } : p)
      );
      toast.error('Došlo je do greške pri ažuriranju eksternog proizvoda.');
    }
  };

  const handleSubscriptionOnlyToggle = async (row, isEnabled) => {
    const productId = row._id;
    try {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isSubscriptionOnlyProduct: isEnabled } : p)
      );
      const { success } = await updateProductIsSubscriptionOnly(productId, isEnabled);
      if (!success) {
        setProducts((prev) =>
          prev.map((p) => p._id === productId ? { ...p, isSubscriptionOnlyProduct: !isEnabled } : p)
        );
        toast.error('Došlo je do greške pri ažuriranju pretplatničkog paketa.');
      } else {
        toast.success('Uspešno ažuriran pretplatnički paket.');
      }
    } catch {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, isSubscriptionOnlyProduct: !isEnabled } : p)
      );
      toast.error('Došlo je do greške pri ažuriranju pretplatničkog paketa.');
    }
  };

  const handleStockStatusToggle = async (row, isInStock) => {
    const productId = row._id;
    const newStatus = isInStock ? 'instock' : 'outofstock';
    const previousStatus = row.stock_status;
    try {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, stock_status: newStatus } : p)
      );
      const { success, message } = await updateProductStockStatus(productId, newStatus);
      if (!success) {
        setProducts((prev) =>
          prev.map((p) => p._id === productId ? { ...p, stock_status: previousStatus } : p)
        );
        toast.error(message || 'Došlo je do greške pri ažuriranju statusa zaliha.');
      } else {
        toast.success('Status zaliha je uspešno ažuriran.');
      }
    } catch {
      setProducts((prev) =>
        prev.map((p) => p._id === productId ? { ...p, stock_status: previousStatus } : p)
      );
      toast.error('Došlo je do greške pri ažuriranju statusa zaliha.');
    }
  };

  const handleRestore = async (row) => {
    const { success, message } = await restoreProduct(row._id);
    if (success) {
      toast.success('Proizvod je uspešno vraćen.');
      loadProducts();
    } else {
      toast.error(message || 'Došlo je do greške pri vraćanju proizvoda.');
    }
  };

  const handleConfirmHide = async () => {
    if (!hideTarget) return;
    setHiding(true);
    const { success, message } = await hideProduct(hideTarget._id);
    if (success) {
      toast.success('Proizvod je uspešno sakriven.');
      setHideTarget(null);
      loadProducts();
    } else {
      toast.error(message || 'Došlo je do greške pri sakrivanju proizvoda.');
    }
    setHiding(false);
  };

  const columns = [
    {
      key: 'name',
      label: 'Proizvod',
      render: (val, row) => {
        const truncated = val && val.length > 32 ? val.slice(0, 32) + '..' : val;
        const status = row?.stock_status || 'instock';
        return (
          <span title={val}>
            <span
              className={`${styles.stockDot} ${styles[status] || ''}`}
              title={stockStatusTranslations[status] || status}
            />
            {truncated}
          </span>
        );
      },
    },
    {
      key: 'categories',
      label: 'Kategorija',
      render: (val) => (
        <span>
          {val && val.length > 0 ? val.map((c) => c?.name).join(', ') : '-'}
        </span>
      ),
    },
    {
      key: 'price',
      label: 'Cena',
      render: (val, row) => (
        <>
          <span>{formatPriceWithCurrency(row?.regular_price || 0)}</span>
          <p className="vipPrice">VIP: {formatPriceWithCurrency(row?.vip_price || 0)}</p>
        </>
      ),
    },
    {
      key: 'quickSettings',
      label: '',
      render: (val, row) => (
        <Button
          className="btn-tertiary"
          onClick={() => setQuickSettingsTarget(row)}
        >
          Brza podešavanja
        </Button>
      ),
    },
  ];

  const actions = [
    {
      key: 'edit',
      label: 'Uredi proizvod',
      icon: <img src="/assets/icons/edit-icon.svg" alt="Uredi" />,
      onClick: (row) => navigate(`/proizvodi/izmeni/${row._id}`),
    },
    {
      key: 'stock-toggle',
      label: (row) => (
        <div onClick={(e) => e.stopPropagation()}>
          <ToggleSwitch
            checked={(row.stock_status || 'instock') === 'instock'}
            onChange={(e) => handleStockStatusToggle(row, e.target.checked)}
            size="md"
            label={(row.stock_status || 'instock') === 'instock' ? 'Označi kao rasprodato' : 'Označi na zalihama'}
          />
        </div>
      ),
      onClick: () => {},
      className: 'toggle-switch-item',
    },
    {
      key: 'hide',
      label: 'Sakrij proizvod',
      icon: <img src="/assets/icons/delete.svg" alt="Sakrij" />,
      onClick: (row) => setHideTarget(row),
      visible: (row) => !row.isDeleted,
      className: 'delete',
      danger: true,
    },
    {
      key: 'restore',
      label: 'Vrati proizvod',
      icon: <img src="/assets/icons/verification.svg" alt="Vrati" />,
      onClick: handleRestore,
      visible: (row) => !!row.isDeleted,
    },
  ];

  return (
    <div className={styles.webshop__products}>
      <div className={styles.header}>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', width: '100%', flexWrap: 'wrap' }}>
          <div className={styles.stockLegend}>
            {Object.entries(stockStatusTranslations).map(([status, label]) => (
                <span key={status} className={styles.legendItem}>
              <span className={`${styles.stockDot} ${styles[status] || ''}`} />
                  {label}
            </span>
            ))}
          </div>
          <Radio.Group value={view} onChange={handleViewChange} optionType="button">
            <Radio.Button value="active">Aktivni</Radio.Button>
            <Radio.Button value="hidden">Sakriveni</Radio.Button>
          </Radio.Group>
          <Button
            className="btn-primary"
            type="primary"
            onClick={() => navigate('/proizvodi/dodaj')}
          >
            + Dodaj proizvod
          </Button>
        </div>
      </div>
      <div className={`${styles.body} products_wrapper`}>
        {loading ? (
          <Spinner size="lg" />
        ) : (
          <>
            <GenericTable
              data={products}
              columns={columns}
              actions={actions}
              rowClassName={(row) => (row.isDeleted ? styles.deletedRow : '')}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>

      <DeleteModal
        visible={!!hideTarget}
        onDelete={handleConfirmHide}
        onCancel={() => setHideTarget(null)}
        loading={hiding}
        title="Sakrivanje proizvoda"
        message={`Da li ste sigurni da želite da sakrijete proizvod "${hideTarget?.name}"? Proizvod će biti uklonjen iz aktivnih pretplata.`}
        buttonText="Sakrij proizvod"
        cancelText="Odustani"
      />

      <ProductQuickSettingsModal
        visible={!!quickSettingsTarget}
        product={products.find((p) => p._id === quickSettingsTarget?._id) ?? quickSettingsTarget}
        onClose={() => setQuickSettingsTarget(null)}
        onToggleHide={handleHideToggle}
        onToggleConfigurationProduct={handleConfigurationProductToggle}
        onToggleMix4IndividuallySellable={handleMix4IndividuallySellableToggle}
        onToggleQuantityDiscount={handleQuantityDiscountToggle}
        onToggleEligibleToBuyWithPoints={handleEligibleToBuyWithPointsToggle}
        onToggleExternalProduct={handleExternalProductToggle}
        onToggleSubscriptionOnly={handleSubscriptionOnlyToggle}
        onToggleFrozenProduct={handleFrozenProductToggle}
        onToggleSkipReferredDiscount={handleSkipReferredDiscountToggle}
      />
    </div>
  );
};

const tabItems = [
  { key: 'proizvodi', label: 'Proizvodi', children: <ProizvodiTab /> },
  { key: 'kategorije', label: 'Kategorije', children: <CategoriesTable /> },
  { key: 'pretplatnicki-paketi', label: 'Pretplatnički paketi', children: <SubscriptionPackagesTable /> },
];

const ProductsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(queryParams.get('tab') || 'proizvodi');

  const handleTabChange = (key) => {
    setActiveTab(key);
    const params = new URLSearchParams(location.search);
    params.set('tab', key);
    navigate({ search: params.toString() }, { replace: true });
  };

  return (
    <ContentLayout pt={true}>
      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} className="medics_tab" />
    </ContentLayout>
  );
};

export default ProductsPage;
